import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth';
import { useOperationsAccess } from '../core/useOperationsAccess';
import { useOperationsScopeUx } from '../core/operationsScopeUx';
import { Vessel } from '../core/_models';
import { toast } from 'react-toastify';
import { KTSVG } from '../../../../_metronic/helpers';
import { createVoyage, getVoyageNumber, getVoyagesByVessel } from '../core/_requests';
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import PortSelect from '../../../shared/components/PortSelect'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels, NO_ACCESSIBLE_VESSELS_MESSAGE } from '../../../shared/access/accessibleVessels'

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onVoyageAdded: () => void;
}

const AddVoyageModal: React.FC<Props> = ({ onClose, isOpen, onVoyageAdded }) => {
    const [voyageNumber, setVoyageNumber] = useState('');
    const [vessels, setVessels] = useState<Vessel[]>([]);
    const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);
    const [departurePort, setDeparturePort] = useState('');
    const [arrivalPort, setArrivalPort] = useState('');
    const [charteredVoyageNumber, setCharteredVoyageNumber] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [securityLevel, setSecurityLevel] = useState('');
    const [iceVoyage, setIceVoyage] = useState(false);
    const [eca, setEca] = useState(false);
    const [cosp, setCosp] = useState('');
    const [eosp, setEosp] = useState('');
    const [arrivalFew, setArrivalFew] = useState('');
    const [eta, setEta] = useState('');
    const [estimatedDistance, setEstimatedDistance] = useState('');
    const [displacement, setDisplacement] = useState('');
    const [draftFore, setDraftFore] = useState('');
    const [draftMid, setDraftMid] = useState('');
    const [draftAft, setDraftAft] = useState('');
    const [totalCargoOnboard, setTotalCargoOnboard] = useState('');
    const [loadingCondition, setLoadingCondition] = useState('');
    const [chartererName, setChartererName] = useState('');
    const [chartererNo, setChartererNo] = useState('');
    const [cpSpeed, setCpSpeed] = useState('');
    const [cpConsumptionTotal, setCpConsumptionTotal] = useState('');

    const [agentName, setAgentName] = useState('');
    const [agentContactNumber, setAgentContactNumber] = useState('');
    const [agentTelephoneNumber, setAgentTelephoneNumber] = useState('');
    const [agentFaxAddress, setAgentFaxAddress] = useState('');
    const [agentEmail, setAgentEmail] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitLockRef = useRef(false);
    // const roleEntityId = sessionStorage.getItem("roleEntityId");
    // const roleId = sessionStorage.getItem("roleId");
    const { currentUser } = useAuth()
    const { isSuperAdmin } = useOperationsAccess()
    const scopeUx = useOperationsScopeUx(isSuperAdmin)
    const roleEntityId = currentUser?.roleEntityId;
    const vesselId = currentUser?.vessel?.id;

    const formatDateTimeLocal = (dateTimeValue?: string) => {
        if (!dateTimeValue) return '';
        const normalized = dateTimeValue.replace(' ', 'T');
        return normalized.length >= 16 ? normalized.slice(0, 16) : normalized;
    };

    useEffect(() => {
        fetchVessels();
        if (scopeUx.isCrew && vesselId) {
            fetchVoyageNumber(vesselId)
            fetchLastArrivalDate(vesselId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchVessels = async () => {
        try {
            const vesselList = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser());
            setVessels(vesselList);
        } catch (error) {
            console.error('Failed to fetch vessel list:', error);
        }
    };

    const fetchVoyageNumber = async (vesselId: number) => {
        try {
            const response = await getVoyageNumber(vesselId);

            console.log(voyageNumber);
            setVoyageNumber(response.voyageNumber);
        } catch (error) {
            console.error('Failed to fetch vessel list:', error);
        }
    };

    const fetchLastArrivalDate = async (vesselId: number) => {
        try {
            const voyages = await getVoyagesByVessel(vesselId);
            if (!voyages || voyages.length === 0) return;

            const latestWithArrival = [...voyages]
                .filter((voyage) => !!voyage?.endDate)
                .sort((a, b) => {
                    const dateA = new Date(a.startDate || a.endDate).getTime();
                    const dateB = new Date(b.startDate || b.endDate).getTime();
                    return dateB - dateA;
                })[0];

            if (latestWithArrival?.endDate) {
                setStartDate(formatDateTimeLocal(latestWithArrival.endDate));
            }
        } catch (error) {
            console.error('Failed to fetch latest voyage arrival datetime:', error);
        }
    };

    const handleSubmit = async () => {
        if (submitLockRef.current) {
            return;
        }

        const isMultiVesselRole = scopeUx.isCompanyAdmin || scopeUx.isCompanyGroupAdmin || isSuperAdmin || scopeUx.isOperator;
        if (isMultiVesselRole && !selectedVessel?.id) {
            setError('Please select a vessel.');
            toast.error('Please select a vessel.');
            return;
        }

        if (!voyageNumber || !voyageNumber.trim()) {
            setError('Voyage Number is required.');
            toast.error('Voyage Number is required.');
            return;
        }

        if (!departurePort || !departurePort.trim()) {
            setError('Departure Port is required.');
            toast.error('Departure Port is required.');
            return;
        }

        // Arrival Port is now optional — no validation needed

        if (!startDate) {
            setError('Start Date & Time is required.');
            toast.error('Start Date & Time is required.');
            return;
        }

        if (endDate && startDate && new Date(endDate).getTime() < new Date(startDate).getTime()) {
            setError('End Date cannot be earlier than Start Date.');
            toast.error('End Date cannot be earlier than Start Date.');
            return;
        }

        setError('');
        submitLockRef.current = true;
        setIsSubmitting(true);

        try {
            const resolvedVesselId =
                scopeUx.isCompanyAdmin || scopeUx.isCompanyGroupAdmin || isSuperAdmin || scopeUx.isOperator
                    ? selectedVessel?.id
                    : currentUser?.vessel?.id;

            if (!resolvedVesselId) {
                setError('Please select a vessel.');
                toast.error('Please select a vessel.');
                return;
            }

            const companyId = scopeUx.isOperator ? currentUser?.companyGroupAdminId :
                scopeUx.isCompanyAdmin || scopeUx.isCompanyGroupAdmin || isSuperAdmin
                    ? (selectedVessel?.companyGroupAdmin?.id || selectedVessel?.companyAdmin?.cga?.id)
                    : currentUser?.vessel?.companyGroupAdminId;

            const selectedCaId = scopeUx.isCompanyGroupAdmin || isSuperAdmin || scopeUx.isCompanyAdmin
                ? selectedVessel?.companyAdmin?.id
                : currentUser?.vessel?.companyAdminId;

            await createVoyage(
                voyageNumber,
                departurePort,
                arrivalPort || undefined,
                startDate,
                endDate,
                arrivalPort?.trim() ? `${departurePort} - ${arrivalPort.trim()}` : departurePort,
                Number(resolvedVesselId),
                companyId ? Number(companyId) : undefined,
                charteredVoyageNumber || undefined,
                status,
                securityLevel,
                iceVoyage,
                eca,
                cosp,
                eosp,
                arrivalFew,
                eta,
                estimatedDistance,
                displacement,
                draftFore,
                draftMid,
                draftAft,
                totalCargoOnboard,
                loadingCondition,
                chartererName,
                chartererNo,
                cpSpeed,
                cpConsumptionTotal,
                agentName,
                agentContactNumber,
                agentTelephoneNumber,
                agentFaxAddress,
                agentEmail,
                selectedCaId ? Number(selectedCaId) : undefined
            );

            toast.success("Voyage added successfully");

            onVoyageAdded();
            clearForm();
            onClose();
        } catch (err: any) {
            console.error('Failed to add voyage:', err);
            const userMsg = getApiErrorMessage(err, 'Failed to add voyage. Please check the inputs and try again.');
            setError(userMsg);
            toast.error(userMsg);
        } finally {
            submitLockRef.current = false;
            setIsSubmitting(false);
        }
    };

    const clearForm = () => {
        setVoyageNumber('');
        setDeparturePort('');
        setArrivalPort('');
        setCharteredVoyageNumber('');
        setStartDate('');
        setEndDate('');
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: '70rem' }}
            >
                <div className="custom-modal-header d-flex justify-content-between align-items-center">
                    <h5 className="m-0">Add Voyage</h5>
                    <button className="close-btn" onClick={onClose}>
                        <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
                    </button>
                </div>

                <div className="custom-modal-body">
                    {vessels.length === 0 && (
                        <div className="alert alert-warning">{NO_ACCESSIBLE_VESSELS_MESSAGE}</div>
                    )}
                    {error && <div className="alert alert-danger">{error}</div>}

                    <div className="row">
                        {(scopeUx.isCompanyAdmin || scopeUx.isCompanyGroupAdmin || isSuperAdmin || scopeUx.isOperator) && <div className="col-md-4 mb-3">
                            <label htmlFor="vessel" className="modal_label">Select Vessel <span className="text-danger">*</span></label>
                            <SearchableSelect
                                id="vessel"
                                className="form-control"
                                value={selectedVessel?.id}
                                onChange={(e) => {
                                    const vesselId = Number(e.target.value);
                                    const vessel = vessels.find(v => v.id === vesselId) || null;
                                    setSelectedVessel(vessel);
                                    fetchVoyageNumber(vesselId);
                                    fetchLastArrivalDate(vesselId);
                                }}                            >
                                <option value="">-- Select Vessel --</option>
                                {vessels.map((vessel) => (
                                    <option key={vessel.id} value={vessel.id}>
                                        {vessel.fleet_name}
                                    </option>
                                ))}
                            </SearchableSelect>
                        </div>}
                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Voyage Number <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                value={voyageNumber}
                                onChange={(e) => setVoyageNumber(e.target.value)}
                                readOnly
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Departure Port <span className="text-danger">*</span></label>
                            <PortSelect
                                value={departurePort}
                                onChange={(e) => setDeparturePort(e.target.value)}
                                placeholder="Select or search departure port..."
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Arrival Port <small className="text-muted">(Optional)</small></label>
                            <PortSelect
                                value={arrivalPort}
                                onChange={(e) => setArrivalPort(e.target.value)}
                                placeholder="Select or search arrival port..."
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Departure Datetime <span className="text-danger">*</span></label>
                            <input
                                type="datetime-local"
                                className="form-control"
                                value={startDate ? formatDateTimeLocal(startDate) : ''}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Arrival Datetime </label>
                            <input
                                type="datetime-local"
                                className="form-control"
                                value={endDate ? formatDateTimeLocal(endDate) : ''}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Status</label>
                            <input type="text" className="form-control" value={status} onChange={(e) => setStatus(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Chartered Voyage Number <small className="text-muted">(Optional)</small></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. CH-VYG-2026@01"
                                value={charteredVoyageNumber}
                                onChange={(e) => setCharteredVoyageNumber(e.target.value)}
                            />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Security Level</label>
                            <input type="text" className="form-control" value={securityLevel} onChange={(e) => setSecurityLevel(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Ice Voyage</label>
                            <input type="checkbox" className="form-check-input" checked={iceVoyage} onChange={(e) => setIceVoyage(e.target.checked)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">ECA</label>
                            <input type="checkbox" className="form-check-input" checked={eca} onChange={(e) => setEca(e.target.checked)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">COSP</label>
                            <input type="datetime-local" className="form-control" value={cosp ? formatDateTimeLocal(cosp) : ''} onChange={(e) => setCosp(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">EOSP</label>
                            <input type="datetime-local" className="form-control" value={eosp ? formatDateTimeLocal(eosp) : ''} onChange={(e) => setEosp(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Arrival FEW</label>
                            <input type="datetime-local" className="form-control" value={arrivalFew ? formatDateTimeLocal(arrivalFew) : ''} onChange={(e) => setArrivalFew(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">ETA</label>
                            <input type="datetime-local" className="form-control" value={eta ? formatDateTimeLocal(eta) : ''} onChange={(e) => setEta(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Estimated Distance</label>
                            <input type="number" className="form-control" value={estimatedDistance} onChange={(e) => setEstimatedDistance(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Displacement</label>
                            <input type="number" className="form-control" value={displacement} onChange={(e) => setDisplacement(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Draft Fore</label>
                            <input type="number" className="form-control" value={draftFore} onChange={(e) => setDraftFore(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Draft Mid</label>
                            <input type="number" className="form-control" value={draftMid} onChange={(e) => setDraftMid(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Draft Aft</label>
                            <input type="number" className="form-control" value={draftAft} onChange={(e) => setDraftAft(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Total Cargo Onboard</label>
                            <input type="number" className="form-control" value={totalCargoOnboard} onChange={(e) => setTotalCargoOnboard(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Loading Condition</label>
                            <input type="text" className="form-control" value={loadingCondition} onChange={(e) => setLoadingCondition(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Charterer Name</label>
                            <input type="text" className="form-control" value={chartererName} onChange={(e) => setChartererName(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Charterer No</label>
                            <input type="text" className="form-control" value={chartererNo} onChange={(e) => setChartererNo(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">CP Speed</label>
                            <input type="number" className="form-control" value={cpSpeed} onChange={(e) => setCpSpeed(e.target.value)} />
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="modal_label">CP Consumption Total</label>
                            <input type="number" className="form-control" value={cpConsumptionTotal} onChange={(e) => setCpConsumptionTotal(e.target.value)} />
                        </div>
                    </div>
                    {/* Agent Details Section */}
                    <div className="row mt-5 mb-4">
                        <h6 className="fw-bold mb-3">Agent Details</h6>
                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Name of Agent</label>
                            <input type="text" className="form-control" value={agentName} onChange={(e) => setAgentName(e.target.value)} />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Contact Number</label>
                            <input type="text" className="form-control" value={agentContactNumber} onChange={(e) => setAgentContactNumber(e.target.value)} />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="modal_label">Telephone Number</label>
                            <input type="text" className="form-control" value={agentTelephoneNumber} onChange={(e) => setAgentTelephoneNumber(e.target.value)} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="modal_label">Fax Address</label>
                            <input type="text" className="form-control" value={agentFaxAddress} onChange={(e) => setAgentFaxAddress(e.target.value)} />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="modal_label">Email</label>
                            <input type="email" className="form-control" value={agentEmail} onChange={(e) => setAgentEmail(e.target.value)} />
                        </div>
                    </div>

                </div>

                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn_secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn_success" onClick={handleSubmit} disabled={isSubmitting || vessels.length === 0}>
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );

};


export default AddVoyageModal;